import HtmlDiff from 'htmldiff-js';
import { PolicyComparison, SectionComparison, SubSectionComparison } from './policy_comparison';
import { get, intersection, isArray, isPlainObject } from 'lodash';
import { DraftPolicy, DraftPolicySection, DraftPolicySubSection } from 'views/App/Portal/Policy/_types/manuals/draft';
import draftManualHelper from '../../../helpers/draftManualHelper';

export default class policyComparer {
  static compare = (source: DraftPolicy, target: DraftPolicy, policyNumber: string) => {
    const sourcePolicy = this.scrubPolicy(source);
    const targetPolicy = this.scrubPolicy(target);
    const results: PolicyComparison = {
      ...source,
      sections: [],
      title: HtmlDiff.execute(sourcePolicy.title, targetPolicy.title),
      comparison: '',
      diffs: [],
    };
    results.sections = this.compareSections(sourcePolicy, targetPolicy, policyNumber);

    return results;
  };

  static compareSections = (source: DraftPolicy, target: DraftPolicy, policyNumber: string) => {
    const results: SectionComparison[] = [];
    const sections: any = {};

    const sourceIds = source && source.sections ? source.sections.map((x) => x.id) : [];
    const targetIds = target && target.sections ? target.sections.map((x) => x.id) : [];
    let matchMode = 'title';
    if (intersection(sourceIds, targetIds).length > 0) matchMode = 'id';

    if (source?.sections)
      source.sections.forEach(
        (x: any) => (sections[get(x, matchMode)] = { ...sections[get(x, matchMode)], source: x })
      );
    if (target?.sections)
      target.sections.forEach(
        (x: any) => (sections[get(x, matchMode)] = { ...sections[get(x, matchMode)], target: x })
      );

    Object.values(sections).forEach((sec: any) => {
      const result: SectionComparison = {
        ...sec.source,
        title: HtmlDiff.execute(
          draftManualHelper.formatPolicyNodeTitle(sec.source, policyNumber, source),
          draftManualHelper.formatPolicyNodeTitle(sec.target, policyNumber, target)
        ),
        comparison: '',
        content: HtmlDiff.execute(sec.source?.content ?? '', sec.target?.content ?? ''),
        subSections: this.compareSubSections(sec.source, sec.target, policyNumber, source, target),
      };
      if (sec.source && sec.target) {
        let diffs = [...this.compareNodes(sec.source, sec.target), ...result.subSections.flatMap((x) => x.diffs ?? [])];
        result.diffs = diffs;
        if (diffs.length < 1) {
          result.comparison = 'same';
        } else {
          result.comparison = 'diff';
        }
      } else if (sec.source && !sec.target) {
        result.comparison = 'removed';
      } else {
        result.comparison = 'added';
        result.id = sec.target?.id;
      }
      results.push(result);
    });

    return results;
  };

  static compareSubSections = (
    source: DraftPolicySection,
    target: DraftPolicySection,
    policyNumber: string,
    sourcePolicy: DraftPolicy,
    targetPolicy: DraftPolicy
  ) => {
    const results: SubSectionComparison[] = [];
    const subSections: any = {};

    const sourceIds = source && source.subSections ? source.subSections.map((x) => x.id) : [];
    const targetIds = target && target.subSections ? target.subSections.map((x) => x.id) : [];
    let matchMode = 'title';
    if (intersection(sourceIds, targetIds).length > 0) matchMode = 'id';

    if (source?.subSections)
      source.subSections.forEach(
        (x: any) => (subSections[get(x, matchMode)] = { ...subSections[get(x, matchMode)], source: x })
      );
    if (target?.subSections)
      target.subSections.forEach(
        (x: any) => (subSections[get(x, matchMode)] = { ...subSections[get(x, matchMode)], target: x })
      );

    Object.values(subSections).forEach((sec: any) => {
      if (sec.source) sec.source.content = this.scrubContent(sec.source.content);
      if (sec.target) sec.target.content = this.scrubContent(sec.target.content);
      const result: SubSectionComparison = {
        ...sec.source,
        title: HtmlDiff.execute(
          draftManualHelper.formatPolicyNodeTitle(sec.source, policyNumber, sourcePolicy),
          draftManualHelper.formatPolicyNodeTitle(sec.target, policyNumber, targetPolicy)
        ),
        comparison: '',
        content: HtmlDiff.execute(sec.source?.content ?? '', sec.target?.content ?? ''),
        diffs: [],
      };
      if (sec.source && sec.target) {
        const diffs = this.compareNodes(sec.source, sec.target);
        result.diffs = diffs;
        if (diffs.length > 1) {
          result.comparison = 'diff';
        } else {
          result.comparison = 'same';
        }
      } else if (sec.source && !sec.target) {
        result.comparison = 'removed';
      } else {
        result.comparison = 'added';
        result.id = sec.target?.id;
      }
      results.push(result);
    });

    return results;
  };

  static formatLabel = (node: any, policyNumber: string) => {
    const parts = [];
    if (node?.heading) parts.push(node.heading);
    parts.push(node?.title ?? '');
    return parts.join(': ');
  };

  static compareNodes = (source: any, target: any) => {
    // clone nodes
    const sourceNode = this.cloneNode(source);
    const targetNode = this.cloneNode(target);
    // get diffs
    let diffs: string[] = [];
    if ((sourceNode?.title || targetNode?.title) && sourceNode?.title !== targetNode?.title) {
      diffs = [...diffs, HtmlDiff.execute(sourceNode?.title ?? '', targetNode?.title ?? '')];
    }
    if (
      (sourceNode?.content || targetNode?.content) &&
      JSON.stringify(sourceNode?.content) !== JSON.stringify(targetNode?.content)
    ) {
      diffs = [...diffs, HtmlDiff.execute(sourceNode?.content ?? '', targetNode?.content ?? '')];
    }

    return diffs;
  };

  static cloneNode = (node: any, scrub: boolean = true) => {
    const n = JSON.parse(JSON.stringify(node));
    if (scrub) {
      const newNode: any = {};
      const blacklistKeys = ['guid', 'checkSum'];
      if (isPlainObject(n)) {
        Object.entries(n).forEach(([k, v]) => {
          if (!blacklistKeys.includes(k)) {
            let val = v;
            if (isPlainObject(v)) val = this.cloneNode(v);
            if (isArray(v)) val = v.map((x) => this.cloneNode(x));

            newNode[k] = val;
          }
        });
      }
      return newNode;
    }
    return n;
  };

  static scrubPolicy = (policy: DraftPolicy) => {
    if (!policy) return policy;
    policy.sections.forEach((section: DraftPolicySection) => {
      if (section.content) section.content = this.scrubContent(section.content);
      section.subSections.forEach((subSection: DraftPolicySubSection) => {
        if (subSection.content) subSection.content = this.scrubContent(subSection.content);
      });
    });
    return policy;
  };

  static scrubContent = (content: string) => {
    const ctr = document.createElement('div');
    ctr.innerHTML = content;
    const badEls = ctr.querySelectorAll('lexindex');
    badEls.forEach((x) => x.remove());
    const links = ctr.querySelectorAll('a');
    links.forEach((l) => {
      if (l.href.startsWith('xref://') || l.href.startsWith('s3://')) l.href = '';
    });
    if (content !== ctr.innerHTML) {
      // console.log('scrubbed', [ctr.innerHTML, content])
    }
    return ctr.innerHTML;
  };

  static getTitleText = (title: string) => {
    const div = document.createElement('div');
    div.innerHTML = title;
    return div.innerText;
  };
}
